import type { ContactSession, TranscriptMessage } from '@/services/conversationService';
import { dayKey, formatDayLabel, formatOutcome, parseTime } from './conversationFormat';

/**
 * Flattens a contact chat (messages across sessions) into what the timeline
 * renders: a session separator where a session begins ("Mo, 29.09. · Termin
 * gebucht"), a day separator where the day changes inside a session, and the
 * messages with grouping hints for the rhythm of consecutive bubbles.
 */

export type TimelineEntry =
  | {
      type: 'session';
      key: string;
      conversationId: number;
      label: string;
      outcome: string | null;
      isTest: boolean;
    }
  | { type: 'day'; key: string; label: string }
  | {
      type: 'message';
      key: string;
      message: TranscriptMessage;
      /** First bubble of a run by the same sender: shows the sender label, gets more space above. */
      groupStart: boolean;
      /** Last bubble of the run: carries the bubble tail. */
      groupEnd: boolean;
    };

/** Bubbles further apart than this start a new run even from the same sender. */
export const GROUP_GAP_MS = 5 * 60_000;

function senderKey(message: TranscriptMessage): string {
  return message.role === 'staff' ? `staff:${message.author ?? ''}` : message.role;
}

/** Stable React keys: the same message keeps its key when older sessions are prepended. */
export function messageKeys(messages: TranscriptMessage[]): string[] {
  const seen = new Map<string, number>();
  return messages.map((m) => {
    const base = `${m.conversation_id ?? '-'}|${m.ts ?? '-'}|${m.role}`;
    const n = seen.get(base) ?? 0;
    seen.set(base, n + 1);
    return n === 0 ? base : `${base}#${n}`;
  });
}

export function buildChatTimeline(
  messages: TranscriptMessage[],
  sessions: ContactSession[],
  now: number,
): TimelineEntry[] {
  const sessionById = new Map(sessions.map((s) => [s.conversation_id, s]));
  const keys = messageKeys(messages);
  const entries: TimelineEntry[] = [];
  let currentSession: number | null | undefined;
  let currentDay: string | null = null;
  let previous: TranscriptMessage | null = null;

  messages.forEach((message, index) => {
    const conversationId = message.conversation_id ?? currentSession ?? null;
    const day = dayKey(message.ts);

    if (conversationId !== currentSession) {
      currentSession = conversationId;
      const session = conversationId != null ? sessionById.get(conversationId) : undefined;
      if (session) {
        const startedAt = session.started_at ?? message.ts;
        const label = formatDayLabel(startedAt, now) ?? 'Sitzung';
        entries.push({
          type: 'session',
          key: `session-${session.conversation_id}`,
          conversationId: session.conversation_id,
          label,
          outcome: formatOutcome(session.status),
          isTest: !!session.is_test,
        });
        currentDay = dayKey(startedAt) ?? day;
        previous = null;
      }
    }

    if (day && day !== currentDay) {
      const label = formatDayLabel(message.ts, now);
      if (label) entries.push({ type: 'day', key: `day-${conversationId ?? '-'}-${day}`, label });
      currentDay = day;
      previous = null;
    }

    const isEvent = message.role === 'event';
    const gap =
      previous && parseTime(previous.ts) !== null && parseTime(message.ts) !== null
        ? (parseTime(message.ts) as number) - (parseTime(previous.ts) as number)
        : 0;
    const continues =
      !!previous && !isEvent && previous.role !== 'event' && senderKey(previous) === senderKey(message) && gap <= GROUP_GAP_MS;

    if (!continues) {
      const last = entries[entries.length - 1];
      if (last?.type === 'message') entries[entries.length - 1] = { ...last, groupEnd: true };
    }
    entries.push({ type: 'message', key: keys[index], message, groupStart: !continues, groupEnd: true });
    if (continues) {
      const prevIndex = entries.length - 2;
      const prev = entries[prevIndex];
      if (prev?.type === 'message') entries[prevIndex] = { ...prev, groupEnd: false };
    }
    previous = message;
  });

  return entries;
}
